import type { Node2d } from "@logic/lib/mesh"
import { atom, DefaultValue, selector } from "recoil"

import { findNodeState, nodeIndex } from "./nodes"
import type { TransactionOperation } from "./transactions"
import { readonlyTransactors } from "./transactions"

export const nodeSelectionState_INTERNAL = atom<Set<string>>({
  key: `nodeSelection_INTERNAL`,
  default: new Set(),
})

export const getNodeSelectionState: TransactionOperation<
  undefined,
  Set<string>
> = ({ get }) => {
  const nodeIds = get(nodeIndex)
  const internalSelection = get(nodeSelectionState_INTERNAL)

  return new Set([...internalSelection].filter((id) => nodeIds.includes(id)))
}

export const nodeSelectionState = selector<Set<string>>({
  key: `nodeSelection`,
  get: ({ get }) => getNodeSelectionState(readonlyTransactors(get)),
  set: ({ set, reset }, newValue) => {
    if (newValue instanceof DefaultValue) {
      reset(nodeSelectionState_INTERNAL)
    } else {
      set(nodeSelectionState_INTERNAL, newValue)
    }
  },
})

export const nodeSelectionPositionState = selector<Node2d>({
  key: `nodeSelectionPosition`,
  get: () => ({ x: 0, y: 0 }),
  set: ({ set, get }, displacement) => {
    if (displacement instanceof DefaultValue) return
    const selection = get(nodeSelectionState)
    selection.forEach((nodeId) => {
      const nodeState = findNodeState(nodeId)
      set(nodeState, (current) => ({
        x: current.x + displacement.x,
        y: current.y + displacement.y,
      }))
    })
  },
})

export const lastOf = <T>(set: Set<T>): T => Array.from(set)[set.size - 1]

export const lastNodeIdState = selector<string | null>({
  key: `lastNodeId`,
  get: ({ get }) => {
    const nodeSelection = get(nodeSelectionState)

    return lastOf(nodeSelection)
  },
})
